type UnknownRecord = Record<string, unknown>;

export type ApiErrorEnvelope = {
  status?: number;
  code?: string;
  message?: string;
};

const isObject = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null;

export const parseApiErrorEnvelope = (value: unknown): ApiErrorEnvelope | undefined => {
  if (!isObject(value)) return undefined;

  const maybeStatus = value.status;
  const maybeCode = value.code;
  const maybeMessage = value.message;

  const hasEnvelopeShape =
    typeof maybeStatus === 'number' ||
    typeof maybeCode === 'string' ||
    typeof maybeMessage === 'string';

  if (!hasEnvelopeShape) return undefined;

  return {
    status: typeof maybeStatus === 'number' ? maybeStatus : undefined,
    code: typeof maybeCode === 'string' ? maybeCode : undefined,
    message: typeof maybeMessage === 'string' ? maybeMessage : undefined,
  };
};

export const toApiErrorMessage = (
  error: unknown,
  fallbackMessage = 'API request failed'
): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  const envelope = parseApiErrorEnvelope(error);
  if (envelope?.message) {
    return envelope.message;
  }

  if (isObject(error)) {
    const maybeMessage = error.message;
    const maybeError = error.error;
    if (typeof maybeMessage === 'string' && maybeMessage) return maybeMessage;
    if (typeof maybeError === 'string' && maybeError) return maybeError;
  }

  if (typeof error === 'string' && error) {
    return error;
  }

  return fallbackMessage;
};

export const normalizeApiError = (
  error: unknown,
  fallbackMessage = 'API request failed'
): Error => {
  const message = toApiErrorMessage(error, fallbackMessage);
  const envelope = parseApiErrorEnvelope(error);
  const normalized = new Error(message);

  if (envelope?.code) {
    normalized.name = envelope.code;
  }

  return normalized;
};
